import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      navigate(u.role === "admin" ? "/admin" : "/swipe");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12 bg-sand">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Users className="w-10 h-10 text-terracotta mx-auto" />
          <h1 className="font-display text-4xl font-bold text-charcoal mt-3">Welcome back</h1>
          <p className="text-charcoal/60 mt-2">Log in to continue your conversations.</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-border p-8 shadow-sm space-y-5">
          <div>
            <Label className="text-charcoal font-semibold">Email</Label>
            <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 rounded-xl border-border focus-visible:ring-terracotta/50" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-charcoal font-semibold">Password</Label>
            <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 rounded-xl border-border focus-visible:ring-terracotta/50" placeholder="••••••••" />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">
            {loading ? "Logging in..." : "Log in"}
          </Button>
          <div className="flex items-center gap-3 py-1">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-charcoal/40">or</span><div className="h-px bg-border flex-1" />
          </div>
          <button type="button" data-testid="google-login" onClick={googleLogin}
            className="w-full h-11 rounded-full border border-border bg-white hover:bg-sand flex items-center justify-center gap-2 font-semibold text-charcoal transition-colors duration-200">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
          </button>
          <p className="text-center text-sm text-charcoal/60">New here? <Link to="/register" className="text-terracotta font-semibold">Join free</Link></p>
        </form>
      </div>
    </div>
  );
}
